import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ArrowLeft, Clock, Shield, AlertTriangle, Phone, Mail, Globe, MapPin, User, Pencil, Sparkles, Wrench, History, UserPlus, Send, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_STYLE = { open: 'bg-blue-500/10 text-blue-700 dark:text-blue-400', triage: 'bg-violet-500/10 text-violet-700 dark:text-violet-400', in_progress: 'bg-amber-500/10 text-amber-700 dark:text-amber-400', escalated: 'bg-red-500/10 text-red-700 dark:text-red-400', resolved: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400', closed: 'bg-muted text-muted-foreground' };
const PRIO_STYLE = { low: 'bg-muted text-muted-foreground', medium: 'bg-blue-500/10 text-blue-700', high: 'bg-amber-500/10 text-amber-700', critical: 'bg-red-500/10 text-red-700' };
const SLA_STYLE = { within_sla: 'bg-emerald-500/10 text-emerald-700', at_risk: 'bg-amber-500/10 text-amber-700', breached: 'bg-red-500/10 text-red-700', exception: 'bg-muted text-muted-foreground' };
const SLA_ICON = { within_sla: Shield, at_risk: AlertTriangle, breached: Clock, exception: Shield };
const SOURCE_ICON = { call: Phone, email: Mail, portal: Globe };
const STATUSES = ['open', 'triage', 'in_progress', 'escalated', 'resolved', 'closed'];

export default function TicketDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [users, setUsers] = useState([]);
  const [products, setProducts] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [journalNote, setJournalNote] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSummary, setAiSummary] = useState('');
  // Dialogs
  const [escDlg, setEscDlg] = useState(false);
  const [partsDlg, setPartsDlg] = useState(false);
  const [reassignDlg, setReassignDlg] = useState(false);
  const [reassignTo, setReassignTo] = useState('');
  const [escForm, setEscForm] = useState({ target: 'agency', reason: '', troubleshooting_steps: '', issue_summary: '', assigned_agency: '' });
  const [partsForm, setPartsForm] = useState({ product_id: '', quantity: 1, reason: '' });

  const load = useCallback(() => {
    api.get(`/tickets/${id}`).then(r => { setTicket(r.data); setAiSummary(r.data.ai_summary || ''); }).catch(() => { toast.error('Ticket not found'); nav('/tickets'); });
  }, [id, nav]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get('/users').then(r => setUsers(r.data.users || []));
    api.get('/products').then(r => setProducts(r.data.products || []));
    api.get('/masters/agency').then(r => setAgencies(r.data.items || []));
  }, []);

  const getUserName = (uid) => users.find(u => u.user_id === uid)?.name || uid || '-';

  const updateStatus = async (status) => {
    await api.put(`/tickets/${id}`, { status });
    toast.success(`Status updated to ${status}`); load();
  };

  const addNote = async () => {
    if (!journalNote.trim()) return;
    await api.post(`/tickets/${id}/journal`, { note: journalNote });
    toast.success('Note added'); setJournalNote(''); load();
  };

  const generateSummary = async () => {
    setAiLoading(true);
    try {
      const r = await api.post(`/tickets/${id}/ai-summary`);
      setAiSummary(r.data.summary);
      toast.success('AI summary generated'); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'AI summary failed'); }
    setAiLoading(false);
  };

  const escalate = async () => {
    try {
      await api.post(`/tickets/${id}/escalate`, escForm);
      toast.success('Escalated'); setEscDlg(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const addParts = async () => {
    try {
      await api.post(`/tickets/${id}/parts`, { ...partsForm, quantity: parseInt(partsForm.quantity) || 1 });
      toast.success('Parts added'); setPartsDlg(false); setPartsForm({ product_id: '', quantity: 1, reason: '' }); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const reassign = async () => {
    await api.put(`/tickets/${id}/reassign`, { assigned_to: reassignTo });
    toast.success('Reassigned'); setReassignDlg(false); setReassignTo(''); load();
  };

  if (!ticket) return <div className="flex items-center justify-center h-64"><Loader2 className="animate-spin" /></div>;

  const SlaIcon = SLA_ICON[ticket.sla_status] || Shield;
  const SrcIcon = SOURCE_ICON[ticket.source] || Globe;
  const journal = [...(ticket.journal || [])].reverse();

  return (
    <div data-testid="ticket-detail-page" className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => nav('/tickets')} data-testid="back-to-tickets"><ArrowLeft size={18} /></Button>
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="font-heading text-xl font-bold">{ticket.title}</h1>
            <Badge variant="outline" className={`text-xs ${STATUS_STYLE[ticket.status]}`}>{ticket.status?.replace('_', ' ')}</Badge>
            <Badge variant="secondary" className={`text-xs ${PRIO_STYLE[ticket.priority]}`}>{ticket.priority}</Badge>
            <Badge variant="secondary" className={`text-xs ${SLA_STYLE[ticket.sla_status]}`}><SlaIcon size={12} className="mr-1 inline" />{ticket.sla_status?.replace('_', ' ')}</Badge>
          </div>
          <p className="text-xs text-muted-foreground font-mono mt-0.5">{ticket.ticket_id} | Created {ticket.created_at?.slice(0, 16)?.replace('T', ' ')}</p>
        </div>
        <div className="flex gap-2">
          <Select value={ticket.status} onValueChange={updateStatus}>
            <SelectTrigger className="w-36" data-testid="detail-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s}>{s.replace('_', ' ')}</SelectItem>)}</SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={() => setReassignDlg(true)} data-testid="detail-reassign-btn"><UserPlus size={14} className="mr-1" /> Reassign</Button>
          <Button variant="outline" size="sm" onClick={() => setEscDlg(true)} className="text-destructive border-destructive/30"><AlertTriangle size={14} className="mr-1" /> Escalate</Button>
          <Button variant="outline" size="sm" onClick={() => setPartsDlg(true)}><Wrench size={14} className="mr-1" /> Parts</Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        {/* Left: Details */}
        <div className="lg:col-span-2 space-y-4">
          <Card className="border border-border">
            <CardContent className="p-4 space-y-3">
              <p className="text-sm whitespace-pre-wrap">{ticket.description || 'No description'}</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm pt-2 border-t border-border">
                <div><span className="text-muted-foreground text-xs block">Source</span><span className="flex items-center gap-1"><SrcIcon size={13} /> {ticket.source}</span></div>
                <div><span className="text-muted-foreground text-xs block">Issue Type</span>{ticket.issue_type || '-'}</div>
                <div><span className="text-muted-foreground text-xs block">City / State</span>{ticket.city || '-'}{ticket.state ? `, ${ticket.state}` : ''}</div>
                <div><span className="text-muted-foreground text-xs block">Retailer</span>{ticket.retailer || '-'}</div>
                <div><span className="text-muted-foreground text-xs block">Store</span>{ticket.store || '-'}</div>
                <div><span className="text-muted-foreground text-xs block">Agency</span>{ticket.assigned_agency || '-'}</div>
                <div><span className="text-muted-foreground text-xs block">Assigned To</span><User size={12} className="inline mr-1" />{getUserName(ticket.assigned_to)}</div>
                <div><span className="text-muted-foreground text-xs block">SLA Due</span>{ticket.sla_due_date?.slice(0, 16)?.replace('T', ' ') || '-'}</div>
                <div><span className="text-muted-foreground text-xs block">Created By</span>{getUserName(ticket.created_by)}</div>
              </div>
              {(ticket.contact_name || ticket.phone_number || ticket.address) && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm pt-2 border-t border-border">
                  <div><span className="text-muted-foreground text-xs block">Contact</span>{ticket.contact_name || '-'}</div>
                  <div><span className="text-muted-foreground text-xs block">Phone</span>{ticket.phone_number || '-'}</div>
                  <div><span className="text-muted-foreground text-xs block">Address</span>{ticket.address || '-'}</div>
                  <div><span className="text-muted-foreground text-xs block">Hours</span>{ticket.working_hours || '-'}</div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* AI Summary */}
          <Card className="border border-border">
            <CardHeader className="pb-2 flex-row items-center justify-between">
              <CardTitle className="text-sm flex items-center gap-1"><Sparkles size={14} className="text-primary" /> AI Summary</CardTitle>
              <Button size="sm" variant="outline" onClick={generateSummary} disabled={aiLoading} data-testid="generate-ai-summary">
                {aiLoading ? <Loader2 size={14} className="animate-spin mr-1" /> : <Sparkles size={14} className="mr-1" />}{aiLoading ? 'Generating...' : 'Generate'}
              </Button>
            </CardHeader>
            <CardContent>
              {aiSummary ? <p className="text-sm whitespace-pre-wrap">{aiSummary}</p> : <p className="text-sm text-muted-foreground">Click "Generate" to create an AI summary of this ticket.</p>}
            </CardContent>
          </Card>

          {/* Parts Used */}
          {ticket.parts_used?.length > 0 && (
            <Card className="border border-border">
              <CardHeader className="pb-2"><CardTitle className="text-sm">Parts Used</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-1">
                  {ticket.parts_used.map((p, i) => (
                    <div key={i} className="flex justify-between text-sm p-2 rounded bg-muted/50">
                      <span>{p.quantity}x {products.find(pr => pr.product_id === p.product_id)?.name || p.product_id}</span>
                      <span className="text-muted-foreground text-xs">{p.reason}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Escalation History */}
          {ticket.escalation_history?.length > 0 && (
            <Card className="border border-destructive/20">
              <CardHeader className="pb-2"><CardTitle className="text-sm text-destructive">Escalation History</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {ticket.escalation_history.map((e, i) => (
                    <div key={i} className="text-sm border-l-2 border-destructive/30 pl-3 py-1">
                      <p className="font-medium">{e.action === 'reassigned' ? `Reassigned: ${e.from} -> ${e.to}` : `Escalated to ${e.target}: ${e.reason}`}</p>
                      <p className="text-xs text-muted-foreground">{e.timestamp?.slice(0, 16)?.replace('T', ' ')} | by {e.user_name || e.user_id || e.by}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right: Journal/Activity */}
        <div className="space-y-4">
          <Card className="border border-border">
            <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-1"><History size={14} /> Activity Journal</CardTitle></CardHeader>
            <CardContent>
              <div className="flex gap-2 mb-3">
                <Input placeholder="Add a note..." value={journalNote} onChange={e => setJournalNote(e.target.value)} onKeyDown={e => e.key === 'Enter' && addNote()} data-testid="detail-note-input" className="text-sm" />
                <Button size="icon" variant="outline" onClick={addNote} disabled={!journalNote.trim()} data-testid="detail-add-note"><Send size={14} /></Button>
              </div>
              <ScrollArea className="max-h-[500px] pr-2">
                <div className="space-y-3">
                  {journal.length === 0 && <p className="text-xs text-muted-foreground text-center py-3">No activity yet</p>}
                  {journal.map((e, i) => (
                    <div key={i} className="text-xs border-l-2 border-primary/20 pl-2.5 py-1">
                      <div className="flex justify-between items-start gap-1">
                        <span className="font-medium capitalize text-foreground">{e.action?.replace(/_/g, ' ')}</span>
                        <span className="text-muted-foreground shrink-0">{e.timestamp?.slice(5, 16)?.replace('T', ' ')}</span>
                      </div>
                      <p className="text-muted-foreground mt-0.5 leading-relaxed">{e.details}</p>
                      {e.user_name && <p className="text-muted-foreground/70 mt-0.5">by {e.user_name}</p>}
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Escalation Dialog */}
      <Dialog open={escDlg} onOpenChange={setEscDlg}>
        <DialogContent className="sm:max-w-lg" data-testid="detail-escalate-dialog">
          <DialogHeader><DialogTitle className="font-heading text-destructive">Escalate Ticket</DialogTitle></DialogHeader>
          <div className="grid gap-3 py-2">
            <div className="space-y-1.5"><Label>Escalate To *</Label>
              <Select value={escForm.target} onValueChange={v => setEscForm(f => ({ ...f, target: v }))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="agency">3rd Party Agency</SelectItem><SelectItem value="meta">Meta (Software)</SelectItem></SelectContent></Select>
            </div>
            {escForm.target === 'agency' && <div className="space-y-1.5"><Label>Agency</Label>
              <Select value={escForm.assigned_agency || 'none'} onValueChange={v => setEscForm(f => ({ ...f, assigned_agency: v === 'none' ? '' : v }))}><SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger><SelectContent><SelectItem value="none">None</SelectItem>{agencies.map(a => <SelectItem key={a.master_id} value={a.name}>{a.name}</SelectItem>)}</SelectContent></Select>
            </div>}
            <div className="space-y-1.5"><Label>Reason *</Label><Input value={escForm.reason} onChange={e => setEscForm(f => ({ ...f, reason: e.target.value }))} /></div>
            <div className="space-y-1.5"><Label>Troubleshooting Steps *</Label><Textarea value={escForm.troubleshooting_steps} onChange={e => setEscForm(f => ({ ...f, troubleshooting_steps: e.target.value }))} rows={3} /></div>
            <div className="space-y-1.5"><Label>Issue Summary *</Label><Textarea value={escForm.issue_summary} onChange={e => setEscForm(f => ({ ...f, issue_summary: e.target.value }))} rows={2} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setEscDlg(false)}>Cancel</Button><Button variant="destructive" onClick={escalate} disabled={!escForm.reason || !escForm.troubleshooting_steps || !escForm.issue_summary}>Escalate</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Parts Dialog */}
      <Dialog open={partsDlg} onOpenChange={setPartsDlg}>
        <DialogContent data-testid="detail-parts-dialog">
          <DialogHeader><DialogTitle className="font-heading">Add Parts Used</DialogTitle></DialogHeader>
          <div className="grid gap-3 py-2">
            <div className="space-y-1.5"><Label>Product *</Label>
              <Select value={partsForm.product_id || 'none'} onValueChange={v => setPartsForm(f => ({ ...f, product_id: v === 'none' ? '' : v }))}><SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger><SelectContent><SelectItem value="none">Select...</SelectItem>{products.map(p => <SelectItem key={p.product_id} value={p.product_id}>{p.name} (Stock: {p.stock_level})</SelectItem>)}</SelectContent></Select>
            </div>
            <div className="space-y-1.5"><Label>Quantity</Label><Input type="number" value={partsForm.quantity} onChange={e => setPartsForm(f => ({ ...f, quantity: e.target.value }))} min="1" /></div>
            <div className="space-y-1.5"><Label>Reason</Label><Input value={partsForm.reason} onChange={e => setPartsForm(f => ({ ...f, reason: e.target.value }))} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setPartsDlg(false)}>Cancel</Button><Button onClick={addParts} disabled={!partsForm.product_id}>Add Parts</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reassign Dialog */}
      <Dialog open={reassignDlg} onOpenChange={setReassignDlg}>
        <DialogContent className="sm:max-w-md" data-testid="detail-reassign-dialog">
          <DialogHeader><DialogTitle className="font-heading">Reassign Ticket</DialogTitle></DialogHeader>
          <div className="space-y-1.5 py-2"><Label>Assign To *</Label>
            <Select value={reassignTo || 'none'} onValueChange={v => setReassignTo(v === 'none' ? '' : v)}>
              <SelectTrigger><SelectValue placeholder="Select user..." /></SelectTrigger>
              <SelectContent><SelectItem value="none">Select...</SelectItem>{users.map(u => <SelectItem key={u.user_id} value={u.user_id}>{u.name} ({u.role})</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setReassignDlg(false)}>Cancel</Button><Button onClick={reassign} disabled={!reassignTo}>Reassign</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
